/**
 * FlowItemStatsPanel — compact inline stats for path / lander / offer rows.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';

import { cn } from '@/lib/utils';
import {
  fmtStatValue,
  metricTone,
  orderMetrics,
} from '@/features/flow-optimizer/components/preview/hover/format';

type Metrics = Record<string, number>;

interface FlowItemStatsPanelProps {
  open: boolean;
  metrics?: Metrics;
  className?: string;
  rangeLabel?: string;
}

export const FlowItemStatsPanel: FC<FlowItemStatsPanelProps> = ({
  open,
  metrics,
  className,
  rangeLabel,
}) => {
  const ordered = useMemo(
    () => (metrics ? orderMetrics(metrics) : []),
    [metrics],
  );

  if (!open) return null;

  return (
    <div
      className={cn(
        'border-t border-border/40 bg-muted/10 py-1.5 pl-7 pr-2',
        className,
      )}
    >
      {!metrics || ordered.length === 0 ? (
        <p className="text-[10px] italic text-muted-foreground">
          No metrics for this item in the selected range.
        </p>
      ) : (
        <>
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <span className="text-[10px] text-muted-foreground">
              {rangeLabel ?? 'Performance'}
            </span>
            <span className="text-[10px] tabular-nums text-muted-foreground/70">
              {ordered.length} stats
            </span>
          </div>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-0.5 sm:grid-cols-3 lg:grid-cols-4">
            {ordered.map((m) => (
              <StatRow key={m.id} id={m.id} label={m.label} value={m.value} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const StatRow: FC<{ id: string; label: string; value: number }> = ({
  id,
  label,
  value,
}) => (
  <li className="flex min-w-0 items-baseline justify-between gap-2 text-[10px] tabular-nums">
    <span className="truncate text-muted-foreground">{label}</span>
    <span className={cn('shrink-0 font-medium', metricTone(id, value))}>
      {fmtStatValue(id, value)}
    </span>
  </li>
);

export default FlowItemStatsPanel;
